import {
  Button,
  ClickAwayListener,
  Divider,
  FormControl,
  InputLabel,
  Paper,
  Popper,
  PopperProps,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { RowenaAvailableVariable } from 'types/api/SystemTenant/RowenaAI/RowenaContentTypes';
import {
  OPERATOR_LABELS,
  RowenaConditionalAttrs,
  RowenaConditionalOperator,
  operatorRequiresValue,
  variablesCompatibleWithOperator,
} from './rowenaConditional';

interface ConditionalPopoverProps {
  anchorEl: PopperProps['anchorEl'];
  variablesByName: Record<string, RowenaAvailableVariable>;
  initialAttrs: RowenaConditionalAttrs | null;
  onSave: (attrs: RowenaConditionalAttrs) => void;
  onDelete: () => void;
  onClose: () => void;
}

const NONE_VALUE = '__none__';
const ALL_OPERATORS: RowenaConditionalOperator[] = [
  'eq',
  'neq',
  'gt',
  'gte',
  'lt',
  'lte',
  'is_set',
  'is_empty',
  'is_not_empty',
];

export default function ConditionalPopover({
  anchorEl,
  variablesByName,
  initialAttrs,
  onSave,
  onDelete,
  onClose,
}: ConditionalPopoverProps) {
  const open = !!anchorEl && !!initialAttrs;

  const [draftVariable, setDraftVariable] = useState<string>(initialAttrs?.variable ?? '');
  const [draftOperator, setDraftOperator] = useState<RowenaConditionalOperator>(
    (initialAttrs?.operator as RowenaConditionalOperator) ?? 'eq'
  );
  const [draftValue, setDraftValue] = useState<string>(initialAttrs?.value ?? '');

  useEffect(() => {
    setDraftVariable(initialAttrs?.variable ?? '');
    setDraftOperator((initialAttrs?.operator as RowenaConditionalOperator) ?? 'eq');
    setDraftValue(initialAttrs?.value ?? '');
  }, [initialAttrs]);

  const allVariables = useMemo(() => Object.values(variablesByName), [variablesByName]);
  const compatibleVariables = useMemo(
    () => variablesCompatibleWithOperator(allVariables, draftOperator),
    [allVariables, draftOperator]
  );

  if (!open || !initialAttrs) return null;

  const requiresValue = operatorRequiresValue(draftOperator);
  const isValid = !!draftVariable && (!requiresValue || draftValue.trim() !== '');
  const isDirty =
    draftVariable !== initialAttrs.variable ||
    draftOperator !== initialAttrs.operator ||
    draftValue !== initialAttrs.value;

  const handleSave = () => {
    onSave({
      variable: draftVariable,
      operator: draftOperator,
      value: requiresValue ? draftValue : '',
      valueKind: 'literal',
    });
    onClose();
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement='bottom-start'
      modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
      popperOptions={{ strategy: 'fixed' }}
      style={{ zIndex: 1500, position: 'fixed' }}
    >
      <ClickAwayListener
        onClickAway={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('.rowena-conditional')) return;
          if (target?.closest('.MuiPopover-root, .MuiModal-root, .MuiTooltip-popper')) return;
          onClose();
        }}
      >
        <Paper elevation={4} sx={{ p: 1.5, minWidth: 320, maxWidth: 420 }}>
          <Typography variant='caption' sx={{ color: '#3B4797', fontWeight: 700, display: 'block', mb: 1 }}>
            Show this block when…
          </Typography>

          <Stack gap={1.25}>
            <FormControl size='small' fullWidth>
              <InputLabel shrink htmlFor='cond-variable'>
                Variable
              </InputLabel>
              <Select
                native
                id='cond-variable'
                label='Variable'
                value={draftVariable || NONE_VALUE}
                onChange={(e) => {
                  const v = e.target.value as string;
                  setDraftVariable(v === NONE_VALUE ? '' : v);
                }}
                sx={{ fontSize: 12, fontFamily: 'monospace' }}
              >
                <option value={NONE_VALUE}>(select a variable)</option>
                {compatibleVariables.map((v) => (
                  <option key={v.name} value={v.name}>
                    @{v.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl size='small' fullWidth>
              <InputLabel shrink htmlFor='cond-operator'>
                Operator
              </InputLabel>
              <Select
                native
                id='cond-operator'
                label='Operator'
                value={draftOperator}
                onChange={(e) => setDraftOperator(e.target.value as RowenaConditionalOperator)}
                sx={{ fontSize: 12 }}
              >
                {ALL_OPERATORS.map((op) => (
                  <option key={op} value={op}>
                    {OPERATOR_LABELS[op]}
                  </option>
                ))}
              </Select>
            </FormControl>

            {requiresValue && (
              <TextField
                label='Compare to'
                size='small'
                fullWidth
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                placeholder='e.g. admin'
                InputLabelProps={{ shrink: true }}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              />
            )}
          </Stack>

          <Divider sx={{ my: 1.5 }} />
          <Stack direction='row' justifyContent='space-between' alignItems='center'>
            <Button
              size='small'
              color='error'
              onClick={() => {
                onDelete();
                onClose();
              }}
              sx={{ textTransform: 'none' }}
            >
              Remove block
            </Button>
            <Stack direction='row' gap={1}>
              <Button size='small' onClick={onClose} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                size='small'
                variant='contained'
                disabled={!isValid || !isDirty}
                onClick={handleSave}
                sx={{ textTransform: 'none' }}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}
